import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ActiveMasterClientOptionDto,
  API_BASE_URL,
  BillingMetricsResponse,
  ClientDocumentDownloadUrlResponse,
  ClientDocumentsPagedResponse,
  ClientDocumentsQueryParams,
  ClientDocumentViewUrlResponse,
  ClientProductsResponse,
  ClientProductAttributeDto,
  CommunicationsResponse,
  normalizeClientProductAttributesResponse,
  pick,
  pickNum,
  pickStr,
  SubClientSummary,
  UpdateSubClientDto
} from '@lr/data-access';

export interface ClientStaffMember {
  staffId: string;
  displayName: string;
  email: string;
  jobTitle?: string | null;
  assignmentTypeName?: string | null;
  assignmentTypeCode?: string | null;
  mobilePhone?: string | null;
}

function normalizeClientStaffResponse(raw: unknown): ClientStaffMember[] {
  if (raw == null) {
    return [];
  }

  let extracted: unknown[] = [];
  if (Array.isArray(raw)) {
    extracted = raw;
  } else if (typeof raw === 'object') {
    const rawObj = raw as Record<string, unknown>;
    if (Array.isArray(rawObj['data'])) {
      extracted = rawObj['data'] as unknown[];
    } else if (Array.isArray(rawObj['Data'])) {
      extracted = rawObj['Data'] as unknown[];
    }
  }

  return extracted.map(item => {
    const o = item as Record<string, unknown>;
    return {
      staffId: String(pickStr(o, 'staffId', 'StaffId') ?? ''),
      displayName: String(pickStr(o, 'displayName', 'DisplayName') ?? ''),
      email: String(pickStr(o, 'email', 'Email') ?? ''),
      jobTitle: pickStr(o, 'jobTitle', 'JobTitle'),
      assignmentTypeName: pickStr(o, 'assignmentTypeName', 'AssignmentTypeName'),
      assignmentTypeCode: pickStr(o, 'assignmentTypeCode', 'AssignmentTypeCode'),
      mobilePhone: pickStr(o, 'mobilePhone', 'MobilePhone')
    };
  });
}

function normalizeBillingMetricsResponse(raw: unknown): BillingMetricsResponse | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  // Gateway responses may come as either direct payload or wrapped in { data: ... }.
  const rawObj = raw as Record<string, unknown>;
  const extracted =
    rawObj['data'] != null && typeof rawObj['data'] === 'object'
      ? (rawObj['data'] as Record<string, unknown>)
      : rawObj['Data'] != null && typeof rawObj['Data'] === 'object'
        ? (rawObj['Data'] as Record<string, unknown>)
        : rawObj;
  const o = extracted as Record<string, unknown>;
  const lastPaymentRaw = pick<unknown>(o, 'lastPayment', 'LastPayment');
  const nextInvoiceRaw = pick<unknown>(o, 'nextInvoice', 'NextInvoice');

  let lastPayment: BillingMetricsResponse['lastPayment'] = null;
  if (lastPaymentRaw != null && typeof lastPaymentRaw === 'object') {
    const lp = lastPaymentRaw as Record<string, unknown>;
    lastPayment = {
      date: pickStr(lp, 'date', 'Date'),
      paymentType: pickStr(lp, 'paymentType', 'PaymentType'),
      value: pickNum(lp, 'value', 'Value')
    };
  }

  let nextInvoice: BillingMetricsResponse['nextInvoice'] = null;
  if (nextInvoiceRaw != null && typeof nextInvoiceRaw === 'object') {
    const ni = nextInvoiceRaw as Record<string, unknown>;
    nextInvoice = {
      invoiceId: pickNum(ni, 'invoiceId', 'InvoiceId'),
      total: pickNum(ni, 'total', 'Total'),
      dueDate: pickStr(ni, 'dueDate', 'DueDate'),
      invoiceCount: pickNum(ni, 'invoiceCount', 'InvoiceCount'),
      status: pickStr(ni, 'status', 'Status')
    };
  }

  // Support both new explicit summary fields and legacy nested fields.
  const lastPaymentAmount = pickNum(o, 'lastPaymentAmount', 'LastPaymentAmount') ?? lastPayment?.value ?? null;
  const lastPaymentDate = pickStr(o, 'lastPaymentDate', 'LastPaymentDate') ?? lastPayment?.date ?? null;
  const pastDueAmount = pickNum(o, 'pastDueAmount', 'PastDueAmount');
  const currentMonthTotalDueAmount = pickNum(
    o,
    'currentMonthTotalDueAmount',
    'CurrentMonthTotalDueAmount'
  );

  return {
    lastPayment,
    nextInvoice,
    lastPaymentAmount,
    lastPaymentDate,
    pastDueAmount,
    currentMonthTotalDueAmount
  };
}

/**
 * Service for managing client-related data
 * Exclusive to HR Portal
 */
@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_BASE_URL);

  /**
   * Get client products by MCode with total active members count
   * Requires Auth0 authentication with client access
   * Auth interceptor automatically adds bearer token
   */
  getClientProducts(mcode: string): Observable<ClientProductsResponse> {
    return this.http.get<ClientProductsResponse>(`${this.apiUrl}/client/${mcode}/products`);
  }

  /**
   * Get effective read-only plan attributes for a client product.
   * Requires Auth0 authentication with client access for mcode.
   */
  getClientProductAttributes(mcode: string, clientProductId: number): Observable<ClientProductAttributeDto[]> {
    return this.http
      .get<unknown>(`${this.apiUrl}/client/${mcode}/products/${clientProductId}/attributes`)
      .pipe(map((raw) => normalizeClientProductAttributesResponse(raw)));
  }

  /**
   * Get subclients by MCode
   * Requires Auth0 authentication with client access
   * Auth interceptor automatically adds bearer token
   */
  getSubclients(mcode: string): Observable<SubClientSummary[]> {
    return this.http.get<SubClientSummary[]>(`${this.apiUrl}/client/${mcode}/subclients`);
  }

  /**
   * Update a subclient
   * Requires Auth0 authentication with client access
   * Auth interceptor automatically adds bearer token
   */
  updateSubClient(mcode: string, ccode: string, dto: UpdateSubClientDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/client/${mcode}/subclients/${ccode}`, dto);
  }

  /**
   * Get all active master clients (for LegalResourcesAdmin)
   * Requires Auth0 authentication
   */
  getMasterClients(): Observable<ActiveMasterClientOptionDto[]> {
    return this.http.get<ActiveMasterClientOptionDto[]>(`${this.apiUrl}/client/master-clients`);
  }

  /**
   * Get billing metrics for a client (last payment + next invoice)
   * Requires CompanyBilling.View permission
   * Returns null (204) when no billing data exists
   */
  getBillingMetrics(mcode: string): Observable<BillingMetricsResponse | null> {
    return this.http
      .get<unknown>(`${this.apiUrl}/client/${mcode}/billing/metrics`)
      .pipe(map(raw => normalizeBillingMetricsResponse(raw)));
  }

  /**
   * Get communications for a client (system-generated alerts and notifications)
   * Requires Company.View permission
   * Returns empty array (204) when no communications exist
   */
  getCommunications(mcode: string): Observable<CommunicationsResponse> {
    return this.http.get<CommunicationsResponse>(`${this.apiUrl}/client/${mcode}/communications`);
  }

  /**
   * Paged client documents (product brochures, contracts, marketing media).
   * Requires Auth0 authentication, Document.View, and client access for mcode.
   */
  getClientDocuments(
    mcode: string,
    query?: ClientDocumentsQueryParams
  ): Observable<ClientDocumentsPagedResponse> {
    let params = new HttpParams();
    if (query?.page != null) {
      params = params.set('page', String(query.page));
    }
    if (query?.pageSize != null) {
      params = params.set('pageSize', String(query.pageSize));
    }
    if (query?.orderByField != null && query.orderByField !== '') {
      params = params.set('orderByField', query.orderByField);
    }
    if (query?.orderBy != null && query.orderBy !== '') {
      params = params.set('orderBy', query.orderBy);
    }
    if (query?.documentCategory != null && query.documentCategory !== '') {
      params = params.set('documentCategory', String(query.documentCategory));
    }
    if (query?.fileName != null && query.fileName !== '') {
      params = params.set('fileName', query.fileName);
    }
    return this.http.get<ClientDocumentsPagedResponse>(
      `${this.apiUrl}/client/${mcode}/documents`,
      { params }
    );
  }

  /**
   * Resolve SAS view URL for a document
   */
  getClientDocumentViewUrl(
    mcode: string,
    marketingFileId: number
  ): Observable<ClientDocumentViewUrlResponse> {
    return this.http.get<ClientDocumentViewUrlResponse>(
      `${this.apiUrl}/client/${mcode}/documents/${marketingFileId}/view-url`
    );
  }

  /**
   * Resolve download URL for a document
   */
  getClientDocumentDownloadUrl(
    mcode: string,
    marketingFileId: number
  ): Observable<ClientDocumentDownloadUrlResponse> {
    return this.http.get<ClientDocumentDownloadUrlResponse>(
      `${this.apiUrl}/client/${mcode}/documents/${marketingFileId}/download-url`
    );
  }

  /**
   * Legal Resources staff assigned to this client (directory contacts).
   * Requires Company.View permission and client access for mcode.
   */
  getStaff(mcode: string, cCode?: string): Observable<ClientStaffMember[]> {
    let params = new HttpParams();
    if (cCode) {
      params = params.set('cCode', cCode);
    }
    return this.http
      .get<unknown>(`${this.apiUrl}/client/${mcode}/staff`, { params })
      .pipe(map(raw => normalizeClientStaffResponse(raw)));
  }
}
